import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, postMultipart } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { usePortalParticipants } from "../../context/PortalParticipantsContext";
import { StatusPill, STATUS_PILL_ROW_COMPACT_CLASS } from "../../components/StatusPill";
import { toast } from "sonner";
import { RotateCcw, Plus, MessageCircle, ExternalLink, Download, Check, Trash2, Upload, Send } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { AppAvatar } from "../../components/AppAvatar";
import { messageAuthorAvatarSrc } from "../../lib/portalAvatars";
import {
  blobLooksLikeApiError,
  downloadFilenameFromHeaders,
  isScopeLibraryFile,
  portalFilePacketPath,
  portalFileViewPath,
  readApiErrorDetail,
  resolveUploadedContentType,
  uploadedFileName,
} from "../../lib/portalFiles";
import { FullScreenModal } from "../../components/FullScreenModal";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { cn } from "../../lib/utils";

const APPROVAL_FILTERS = new Set(["pending", "approved", "revision_requested", "all"]);

function useMaxMd() {
  const [match, setMatch] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setMatch(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return match;
}

const ADMIN_APPROVAL_STATUS_MENU = {
  pending: [
    { value: "pending", label: "PENDING" },
    { value: "approved", label: "APPROVED", action: "approve" },
  ],
  revision_requested: [
    { value: "revision_requested", label: "REVISION REQUESTED" },
    { value: "approved", label: "APPROVED", action: "approve" },
  ],
  approved: [{ value: "approved", label: "APPROVED" }],
};

function approvalStatusPillTone(status) {
  if (status === "approved") return "ok";
  if (status === "revision_requested") return "error";
  return "warn";
}

function approvalStatusLabel(status) {
  return (ADMIN_APPROVAL_STATUS_MENU[status] || ADMIN_APPROVAL_STATUS_MENU.pending)[0].label;
}

const ApprovalStatusMenu = ({ approval, onAct }) => {
  const [open, setOpen] = useState(false);
  const options = ADMIN_APPROVAL_STATUS_MENU[approval.status] || ADMIN_APPROVAL_STATUS_MENU.pending;
  const canChange = options.some((opt) => opt.action);

  if (!canChange) {
    return (
      <span
        className={cn(
          "t-pill inline-flex shrink-0 border bg-transparent font-[inherit]",
          approvalStatusPillTone(approval.status) === "ok" && "t-pill-ok",
          approvalStatusPillTone(approval.status) === "warn" && "t-pill-warn",
          approvalStatusPillTone(approval.status) === "error" && "t-pill-error",
          STATUS_PILL_ROW_COMPACT_CLASS
        )}
      >
        {approvalStatusLabel(approval.status)}
      </span>
    );
  }

  const handleSelect = async (opt) => {
    setOpen(false);
    if (opt.value === approval.status || !opt.action) return;
    try {
      await onAct(approval, opt.action);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "t-pill inline-flex shrink-0 cursor-pointer border bg-transparent text-left font-[inherit] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)]",
            approvalStatusPillTone(approval.status) === "ok" && "t-pill-ok",
            approvalStatusPillTone(approval.status) === "warn" && "t-pill-warn",
            approvalStatusPillTone(approval.status) === "error" && "t-pill-error",
            STATUS_PILL_ROW_COMPACT_CLASS
          )}
          data-testid={`approval-status-trigger-${approval.id}`}
          aria-label="Change approval status"
        >
          {approvalStatusLabel(approval.status)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
        data-testid={`approval-status-menu-${approval.id}`}
      >
        <div className="flex flex-col gap-1">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cn(
                "rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]",
                opt.value === approval.status && "border border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]"
              )}
              onClick={() => handleSelect(opt)}
              data-testid={`approval-status-option-${opt.value}-${approval.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function ApprovalsPage({ embedded = false }) {
  const { client, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isMobile = useMaxMd();
  const [approvals, setApprovals] = useState([]);
  const filterParam = searchParams.get("filter");
  const filter = APPROVAL_FILTERS.has(filterParam) ? filterParam : "pending";
  const [active, setActive] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingDeleteApproval, setPendingDeleteApproval] = useState(null);
  const [replacingApprovalId, setReplacingApprovalId] = useState(null);
  const replaceInputRef = useRef(null);
  const replaceTargetRef = useRef(null);
  const params = isAdmin ? `?client_id=${client?.id}` : "";

  const load = async () => {
    if (!client) return;
    const { data } = await api.get(`/portal/approvals${params}`);
    setApprovals(data);
    if (active) {
      const updated = data.find((a) => a.id === active.id);
      if (updated) setActive(updated);
    }
  };

  useEffect(() => { load(); }, [client]);

  useEffect(() => {
    const approvalId = searchParams.get("approval");
    if (!approvalId || approvals.length === 0) return;
    const item = approvals.find((a) => a.id === approvalId);
    if (!item) return;
    if (filter !== "all" && item.status !== filter) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("filter", item.status);
        return next;
      }, { replace: true });
    }
    setActive(item);
    requestAnimationFrame(() => {
      document.querySelector(`[data-testid="approval-${approvalId}"]`)?.scrollIntoView({ block: "nearest" });
    });
  }, [approvals, searchParams, filter, setSearchParams]);

  const setFilter = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("filter", key);
      return next;
    }, { replace: true });
  };

  const openApprovalPanel = (approval) => {
    setActive(approval);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("approval", approval.id);
      return next;
    }, { replace: true });
  };

  const closeApprovalPanel = () => {
    setActive(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("approval");
      return next;
    }, { replace: true });
  };

  const act = async (a, action, comment = "") => {
    await api.post(`/portal/approvals/${a.id}/action`, { action, comment });
    toast.success(action === "approve" ? "Approved" : "Revision requested");
    load();
  };

  const addComment = async (text) => {
    if (!active || !text.trim()) return;
    await api.post(`/portal/approvals/${active.id}/comment`, { text });
    load();
  };

  const openApprovalFile = (approval) => {
    if (!approval?.file_id) return;
    const fileMeta = {
      id: approval.file_id,
      name: approval.title,
      original_filename: approval.title,
      packet_kind: approval.packet_kind || approval.kind,
      note: approval.note,
    };
    const path = isScopeLibraryFile(fileMeta)
      ? portalFilePacketPath(fileMeta, client)
      : portalFileViewPath(fileMeta, client);
    if (!path) {
      toast.error("Could not open file");
      return;
    }
    navigate(path);
  };

  const downloadApprovalFile = async (approval) => {
    if (!approval?.file_id) return;
    try {
      const fallback = uploadedFileName({
        name: approval.title,
        original_filename: approval.title,
      });
      const { data, headers } = await api.get(`/portal/files/item/${approval.file_id}/download`, {
        responseType: "blob",
      });
      if (await blobLooksLikeApiError(data)) {
        throw new Error("Download failed");
      }
      const resolvedName = downloadFilenameFromHeaders(headers, fallback);
      const mime = resolveUploadedContentType(
        { name: resolvedName, original_filename: resolvedName },
        headers["content-type"],
      );
      const blobUrl = URL.createObjectURL(new Blob([data], { type: mime }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resolvedName;
      link.rel = "noopener";
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const raw = await readApiErrorDetail(err);
      toast.error(typeof raw === "string" ? raw : "Could not download file");
    }
  };

  const canOpenApprovalFile = (approval) => Boolean(
    approval?.file_id && (approval.status === "pending" || approval.status === "revision_requested"),
  );

  const canManageApprovalFile = (approval) => isAdmin
    && approval?.file_id
    && (approval.status === "pending" || approval.status === "revision_requested");

  const startReplace = (approval) => {
    replaceTargetRef.current = approval;
    replaceInputRef.current?.click();
  };

  const handleReplaceSelected = async (e) => {
    const upload = e.target.files?.[0];
    const target = replaceTargetRef.current;
    e.target.value = "";
    replaceTargetRef.current = null;
    if (!upload || !target?.file_id) return;
    setReplacingApprovalId(target.id);
    try {
      const fd = new FormData();
      fd.append("file", upload);
      await postMultipart(`/portal/files/${target.file_id}/replace${params}`, fd);
      toast.success("New version uploaded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not upload new version");
    } finally {
      setReplacingApprovalId(null);
    }
  };

  const confirmRemoveApprovalFile = async () => {
    if (!pendingDeleteApproval?.file_id) return;
    const approval = pendingDeleteApproval;
    setPendingDeleteApproval(null);
    try {
      await api.delete(`/portal/files/${approval.file_id}`);
      toast.success("Removed");
      if (active?.id === approval.id) closeApprovalPanel();
      load();
    } catch {
      toast.error("Could not remove file");
    }
  };

  if (!client) return <div className="p-12 t-meta">No client context.</div>;

  const filtered = approvals.filter((a) => filter === "all" ? true : a.status === filter);
  const counts = {
    pending: approvals.filter((a) => a.status === "pending").length,
    approved: approvals.filter((a) => a.status === "approved").length,
    revision_requested: approvals.filter((a) => a.status === "revision_requested").length,
    all: approvals.length,
  };

  const filterButtons = [
    ["all", "All"],
    ["pending", "Pending"],
    ["revision_requested", "Revisions"],
    ["approved", "Approved"],
  ].map(([key, label]) => (
    <button
      key={key}
      type="button"
      onClick={() => setFilter(key)}
      className={`shrink-0 px-3 sm:px-4 py-3 text-sm transition-colors border-b-2 -mb-px whitespace-nowrap min-h-[44px] ${filter === key ? "border-[var(--t-accent)] text-[var(--t-text)]" : "border-transparent text-[var(--t-meta)] hover:text-[var(--t-text)]"}`}
      data-testid={`filter-${key}`}
    >
      {label} <span className="t-meta">({counts[key]})</span>
    </button>
  ));

  return (
    <div className={embedded ? "t-fade-up min-w-0" : "p-4 sm:p-5 md:p-7 t-fade-up min-w-0"} data-testid="approvals-page">
      {!embedded && (
        <>
          <div className="label-eyebrow mb-2">Approvals</div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-10">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight">Review queue.</h1>
            {isAdmin && (
              <button type="button" onClick={() => setShowCreate(true)} className="neo-cta self-start" data-testid="new-approval-btn">
                <Plus size={16} strokeWidth={1.4} /> Request approval
              </button>
            )}
          </div>
        </>
      )}
      {embedded ? (
        <div className="mb-6 flex flex-row items-end justify-between gap-3 min-w-0">
          <div
            className="flex gap-1 border-b border-[var(--t-border)] overflow-x-auto min-w-0 flex-1 [-webkit-overflow-scrolling:touch]"
            data-testid="approval-filters"
          >
            {filterButtons}
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="neo-cta shrink-0"
              data-testid="new-approval-btn"
            >
              <Plus size={16} strokeWidth={1.4} /> Request approval
            </button>
          )}
        </div>
      ) : (
        <div
          className="flex gap-1 mb-6 border-b border-[var(--t-border)] overflow-x-auto min-w-0 [-webkit-overflow-scrolling:touch]"
          data-testid="approval-filters"
        >
          {filterButtons}
        </div>
      )}

      <div className="space-y-3" data-testid="approvals-list">
        {filtered.length === 0 ? (
          <div className="neo-panel t-meta text-center py-12">Queue is clear.</div>
        ) : filtered.map((a) => (
          <div
            key={a.id}
            className={cn(
              "flex flex-col neo-panel p-4 transition-transform duration-[160ms] ease-in-out hover:-translate-y-0.5 sm:p-5",
              active?.id === a.id && "ring-1 ring-[var(--t-accent)]/30",
            )}
            data-testid={`approval-${a.id}`}
          >
            <div className={`mb-2 flex flex-wrap justify-between gap-4 ${a.status === "pending" && !isAdmin ? "items-center" : "items-start"}`}>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex min-w-0 flex-nowrap items-center gap-2 overflow-hidden sm:gap-3">
                  {canManageApprovalFile(a) ? (
                    <div className="group relative min-w-0 flex flex-1 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openApprovalFile(a)}
                        className="min-w-0 flex-1 truncate font-heading !font-light text-lg tracking-tight hover:text-[var(--t-accent)] transition-colors flex items-center gap-2 text-left"
                        data-testid={`open-approval-file-${a.id}`}
                      >
                        <span className="truncate">{a.title}</span>
                        <ExternalLink size={12} strokeWidth={1.4} className="shrink-0" />
                      </button>
                      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => startReplace(a)}
                          disabled={replacingApprovalId === a.id}
                          className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-accent)] disabled:opacity-50"
                          data-testid={`replace-approval-file-${a.id}`}
                          aria-label="Upload new version"
                        >
                          <Upload size={14} strokeWidth={1.4} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDeleteApproval(a)}
                          className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-error)]"
                          data-testid={`delete-approval-file-${a.id}`}
                          aria-label="Delete file"
                        >
                          <Trash2 size={14} strokeWidth={1.4} />
                        </button>
                      </div>
                    </div>
                  ) : canOpenApprovalFile(a) ? (
                    <button
                      type="button"
                      onClick={() => openApprovalFile(a)}
                      className="min-w-0 flex-1 truncate font-heading !font-light text-lg tracking-tight hover:text-[var(--t-accent)] transition-colors flex items-center gap-2 text-left"
                      data-testid={`open-approval-file-${a.id}`}
                    >
                      <span className="truncate">{a.title}</span>
                      <ExternalLink size={12} strokeWidth={1.4} className="shrink-0" />
                    </button>
                  ) : (
                    <h3 className="min-w-0 flex-1 truncate font-heading !font-light text-lg tracking-tight">{a.title}</h3>
                  )}
                  {isAdmin || a.status !== "pending" ? (
                    isAdmin ? (
                      <ApprovalStatusMenu approval={a} onAct={act} />
                    ) : (
                      <StatusPill status={a.status} className={STATUS_PILL_ROW_COMPACT_CLASS} />
                    )
                  ) : null}
                </div>
                <p className="t-meta">{a.description}</p>
              </div>
              {!isAdmin && a.status === "pending" && (
                <div className="flex shrink-0 items-center gap-2 self-center">
                  <StatusPill status={a.status} className={STATUS_PILL_ROW_COMPACT_CLASS} />
                  <button type="button" onClick={() => act(a, "request_revision")} className="t-btn-secondary flex items-center gap-1 !rounded-xl" data-testid={`revise-${a.id}`}>
                    <RotateCcw size={14} strokeWidth={1.4} /> Revise
                  </button>
                  <button type="button" onClick={() => act(a, "approve")} className="neo-cta flex items-center gap-1 !rounded-[var(--t-event-pill-radius)]" data-testid={`approve-${a.id}`}>
                    <Check size={14} strokeWidth={1.4} /> Approve
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="text-[10px] font-light tracking-[0.02em] text-[var(--t-meta)]">
                Requested {new Date(a.created_at).toLocaleDateString()}
              </div>
              <button
                type="button"
                onClick={() => (active?.id === a.id ? closeApprovalPanel() : openApprovalPanel(a))}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1 text-xs transition-colors",
                  active?.id === a.id
                    ? "text-[var(--t-accent)]"
                    : "text-[var(--t-meta)] hover:text-[var(--t-accent)]",
                )}
                data-testid={`view-approval-${a.id}`}
                aria-expanded={active?.id === a.id}
                aria-label={`Comments (${a.comments.length})`}
              >
                <MessageCircle size={14} strokeWidth={1.4} /> {a.comments.length}
              </button>
            </div>
          </div>
        ))}
      </div>

      <Sheet open={Boolean(active)} onOpenChange={(open) => !open && closeApprovalPanel()}>
        <SheetContent
          side={isMobile ? "bottom" : "right"}
          className={cn(
            "flex w-full flex-col gap-0 border-[var(--t-border)] bg-[var(--t-bg)] p-0 shadow-none",
            isMobile
              ? "max-h-[min(88dvh,720px)] rounded-t-[var(--t-event-pill-radius)]"
              : "sm:max-w-md",
          )}
        >
          {active ? (
            <ApprovalActivityPanel
              approval={active}
              onClose={closeApprovalPanel}
              onAct={act}
              onComment={addComment}
              canAct={!isAdmin && active.status === "pending"}
              onDownloadFile={downloadApprovalFile}
              canOpenFile={canOpenApprovalFile(active)}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {showCreate && <CreateApprovalModal clientId={client.id} onClose={() => setShowCreate(false)} onSaved={load} />}

      <input
        ref={replaceInputRef}
        type="file"
        className="sr-only"
        onChange={handleReplaceSelected}
        data-testid="approval-replace-input"
      />

      <AlertDialog open={pendingDeleteApproval !== null} onOpenChange={(open) => !open && setPendingDeleteApproval(null)}>
        <AlertDialogContent
          className="z-[80] max-w-md border border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] shadow-none sm:rounded-[var(--t-event-pill-radius)] gap-5"
          data-testid="approval-delete-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-light tracking-tight text-[var(--t-text)]">
              Delete this file?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm font-light text-[var(--t-meta)]">
              {pendingDeleteApproval ? (
                <>
                  <span className="text-[var(--t-text)]">&ldquo;{pendingDeleteApproval.title}&rdquo;</span> will be removed from the approval queue. You cannot undo this.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 sm:justify-end">
            <AlertDialogCancel className="t-btn-secondary min-h-[44px] border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] hover:opacity-90">
              Cancel
            </AlertDialogCancel>
            <button type="button" className="neo-cta" data-testid="approval-delete-confirm-btn" onClick={confirmRemoveApprovalFile}>
              Delete
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const ApprovalActivityPanel = ({
  approval,
  onClose,
  onAct,
  onComment,
  canAct,
  onDownloadFile,
  canOpenFile,
}) => {
  const [text, setText] = useState("");
  const textareaRef = useRef(null);
  const { user, client } = useAuth();
  const participants = usePortalParticipants();

  const resizeComposer = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => {
    resizeComposer();
  }, [text, resizeComposer]);

  const submitComment = () => {
    if (!text.trim()) return;
    onComment(text);
    setText("");
    const el = textareaRef.current;
    if (el) el.style.height = "auto";
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="approval-detail-panel">
      <SheetHeader className="shrink-0 space-y-1 border-b border-[var(--t-border)] px-4 py-4 pr-12 text-left sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <SheetTitle className="min-w-0 truncate font-heading text-lg font-light tracking-tight text-[var(--t-text)]">
            {approval.title}
          </SheetTitle>
          {canOpenFile ? (
            <button
              type="button"
              onClick={() => onDownloadFile(approval)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--t-event-pill-radius)] text-[var(--t-meta)] transition-colors hover:bg-[var(--t-accent)]/10 hover:text-[var(--t-accent)]"
              aria-label="Download file"
              data-testid={`approval-panel-download-file-${approval.id}`}
            >
              <Download size={14} strokeWidth={1.4} />
            </button>
          ) : null}
        </div>
        {approval.description ? (
          <SheetDescription className="text-xs text-[var(--t-meta)]">
            {approval.description}
          </SheetDescription>
        ) : null}
      </SheetHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        {approval.comments.length === 0 ? (
          <p className="text-xs text-[var(--t-meta)]">No comments yet.</p>
        ) : (
          <div className="space-y-4">
            {approval.comments.map((c) => (
              <div key={c.id} className="flex items-start gap-3">
                <AppAvatar
                  name={c.author_name}
                  src={messageAuthorAvatarSrc(c, { participants, user, client })}
                  size={24}
                />
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 flex items-baseline justify-between gap-2">
                    <span className="text-xs font-heading text-[var(--t-text)]">{c.author_name}</span>
                    <span className="shrink-0 text-[10px] text-[var(--t-meta)]">
                      {new Date(c.ts).toLocaleString()}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap break-all text-sm leading-relaxed text-[var(--t-text)]">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-[var(--t-border)] px-4 pt-3 pb-[max(1rem,calc(0.75rem+env(safe-area-inset-bottom)))] sm:px-6">
        <div className="flex items-center gap-2.5">
          <AppAvatar
            name={user?.name || "Taī"}
            src={messageAuthorAvatarSrc(
              { author_role: user?.role, author_name: user?.name },
              { participants, user, client },
            )}
            size={24}
            className="!size-6 shrink-0"
          />
          <div className="relative min-w-0 flex-1 overflow-hidden neo-panel transition-[border-color] focus-within:border-[var(--t-accent)]">
            <textarea
              ref={textareaRef}
              rows={1}
              className="box-border block min-h-11 w-full max-h-[120px] resize-none overflow-x-hidden overflow-y-auto whitespace-pre-wrap break-all border-0 bg-transparent py-2.5 pl-3 pr-11 text-sm font-light leading-normal text-[var(--t-text)] outline-none placeholder:text-[var(--t-meta)]"
              placeholder="Add a comment…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submitComment();
                }
              }}
              data-testid="comment-textarea"
            />
            <button
              type="button"
              onClick={submitComment}
              disabled={!text.trim()}
              className={cn(
                "absolute inset-y-0 right-1.5 my-auto flex h-8 w-8 items-center justify-center rounded-[var(--t-event-pill-radius)] transition-colors disabled:opacity-30",
                text.trim()
                  ? "text-[var(--t-accent)] hover:bg-[var(--t-accent)]/10"
                  : "text-[var(--t-meta)] hover:text-[var(--t-accent)]",
              )}
              aria-label="Post comment"
              data-testid="post-comment-btn"
            >
              <Send size={15} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {canAct ? (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => {
                onAct(approval, "request_revision", text);
                setText("");
                onClose();
              }}
              className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] min-h-[40px] text-xs"
              data-testid="modal-revise-btn"
            >
              Request revision
            </button>
            <button
              type="button"
              onClick={() => {
                onAct(approval, "approve", text);
                setText("");
                onClose();
              }}
              className="neo-cta !min-h-[40px] !text-[10px]"
              data-testid="modal-approve-btn"
            >
              Approve
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const stripExtension = (filename) => {
  if (!filename) return "";
  const lastDot = filename.lastIndexOf(".");
  return lastDot > 0 ? filename.slice(0, lastDot) : filename;
};

const CreateApprovalModal = ({ clientId, onClose, onSaved }) => {
  const [form, setForm] = useState({ title: "", description: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const selectUploadFile = (file) => {
    setUploadFile(file || null);
    if (file && !form.title.trim()) {
      setForm((prev) => ({ ...prev, title: stripExtension(file.name) }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (uploadFile) {
        const fd = new FormData();
        fd.append("file", uploadFile);
        fd.append("folder", "brand");
        fd.append("name", form.title.trim());
        fd.append("version", "v1");
        fd.append("status", "for_review");
        fd.append("studio_only", "false");
        if (form.description.trim()) fd.append("note", form.description.trim());
        await postMultipart(`/portal/files/upload?client_id=${clientId}`, fd);
        toast.success("Approval requested. Client notified.");
      } else {
        await api.post(`/portal/approvals?client_id=${clientId}`, form);
        toast.success("Approval requested. Client notified.");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not create");
    } finally { setBusy(false); }
  };
  return (
    <FullScreenModal
      onClose={onClose}
      ariaLabel="New approval request"
      testid="create-approval-modal"
      contentClassName="max-h-[90vh] overflow-y-auto"
    >
      <div className="label-eyebrow mb-3">Request</div>
      <h2 className="font-heading text-2xl mb-6 tracking-tight">New approval request</h2>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label-eyebrow block mb-2">Title</label>
          <input className="t-input !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="approval-title-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Description</label>
          <textarea className="t-textarea !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="approval-desc-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">File</label>
          <div className="neo-panel px-4 py-3 flex items-center gap-3 min-w-0">
            {uploadFile ? (
              <div className="group flex min-w-0 flex-1 items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm text-[var(--t-text)]">{uploadFile.name}</span>
                <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <label className={`inline-flex ${busy ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
                    <input
                      type="file"
                      className="sr-only"
                      onChange={(e) => selectUploadFile(e.target.files?.[0] || null)}
                      disabled={busy}
                      data-testid="approval-upload-input"
                    />
                    <span className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-accent)] inline-flex items-center justify-center min-h-[32px] min-w-[32px]">
                      <Upload size={14} strokeWidth={1.4} />
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => selectUploadFile(null)}
                    disabled={busy}
                    className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-error)]"
                    data-testid="approval-upload-clear-btn"
                    aria-label="Remove file"
                  >
                    <Trash2 size={14} strokeWidth={1.4} />
                  </button>
                </div>
              </div>
            ) : (
              <label className={`inline-flex shrink-0 ${busy ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
                <input
                  type="file"
                  className="sr-only"
                  onChange={(e) => selectUploadFile(e.target.files?.[0] || null)}
                  disabled={busy}
                  data-testid="approval-upload-input"
                />
                <span className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] px-4 py-2 text-xs uppercase tracking-wide inline-flex items-center justify-center min-h-[40px] whitespace-nowrap">
                  Choose file
                </span>
              </label>
            )}
          </div>
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] flex-1">Cancel</button>
          <button type="submit" className="neo-cta flex-1 justify-center" disabled={busy} data-testid="approval-submit-btn">{busy ? "Sending…" : "Send request"}</button>
        </div>
      </form>
    </FullScreenModal>
  );
};
